import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaExchangeAlt,
  FaClock,
  FaCheck,
  FaUserTie,
  FaPlus,
  FaSync,
  FaSearch,
  FaTrash,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaGlobe,
  FaSuitcase,
  FaTruckMoving,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner"; 

function Transfers({
  driversData,
  transfersData,
  partnersData,
  fetchData,
  transferSearch,
  setTransferSearch,
  transferStatusFilter,
  setTransferStatusFilter,
  transferDateFilter,
  setTransferDateFilter,
  handleCreateTransfer,
  handleTransferStatusUpdate,
  updatingTransferStatus,
  handleUnassignDriver,
  handleAssignDriverToTransfer,
  assigningDriverToTransfer,
  handleTransferDelete,
  deletingTransferId,
  tourBookingsData,
  expandedTransfer,
  setExpandedTransfer,
}) {
  return (
    <>
      <div className="space-y-6">
        {/* Transfers Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
                <FaExchangeAlt className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Total Transfers</p>
                <p className="text-2xl font-bold text-white">
                  {transfersData.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
                <FaClock className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Pending</p>
                <p className="text-2xl font-bold text-white">
                  {transfersData.filter((t) => t.status === "pending").length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                <FaCheck className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Confirmed</p>
                <p className="text-2xl font-bold text-white">
                  {transfersData.filter((t) => t.status === "confirmed").length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                <FaUserTie className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">With Driver</p>
                <p className="text-2xl font-bold text-white">
                  {transfersData.filter((t) => t.assignedDriver).length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>{" "}
        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Transfer Management
            </h3>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <motion.button
                onClick={() => handleCreateTransfer()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 sm:flex-none px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-lg flex items-center justify-center"
              >
                <FaPlus className="mr-1 sm:mr-2" />
                <span className="text-sm sm:text-base">Create Transfer</span>
              </motion.button>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaSync className="inline mr-2" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search transfers..."
                value={transferSearch}
                onChange={(e) => setTransferSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>

            <select
              value={transferStatusFilter}
              onChange={(e) => setTransferStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <DatePicker
                selected={transferDateFilter.startDate}
                onChange={(date) =>
                  setTransferDateFilter((prev) => ({
                    ...prev,
                    startDate: date,
                  }))
                }
                placeholderText="Start Date"
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                dateFormat="dd/MM/yyyy"
              />
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <DatePicker
                selected={transferDateFilter.endDate}
                onChange={(date) =>
                  setTransferDateFilter((prev) => ({
                    ...prev,
                    endDate: date,
                  }))
                }
                placeholderText="End Date"
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                dateFormat="dd/MM/yyyy"
                minDate={transferDateFilter.startDate}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {transfersData
          .filter((transfer) => {
            const matchesSearch =
              (transfer.name + " " + transfer.surname)
                .toLowerCase()
                .includes(transferSearch.toLowerCase()) ||
              transfer.email
                .toLowerCase()
                .includes(transferSearch.toLowerCase()) ||
              transfer.phone
                .toLowerCase()
                .includes(transferSearch.toLowerCase()) ||
              transfer.pickupLocation
                .toLowerCase()
                .includes(transferSearch.toLowerCase()) ||
              transfer.dropoffLocation
                .toLowerCase()
                .includes(transferSearch.toLowerCase());

            const matchesStatus =
              transferStatusFilter === "all" ||
              transfer.status === transferStatusFilter;

            const matchesDate =
              !transferDateFilter.startDate ||
              !transferDateFilter.endDate ||
              (new Date(transfer.pickUpDate) >= transferDateFilter.startDate &&
                new Date(transfer.pickUpDate) <= transferDateFilter.endDate);

            return matchesSearch && matchesStatus && matchesDate;
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((transfer) => (
            <motion.div
              key={transfer._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
            >
              <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  {/* Customer Info */}
                  <div className="flex items-start space-x-4">
                    <div
                      className={`bg-${
                        transfer.status === "confirmed"
                          ? "green"
                          : transfer.status === "completed"
                          ? "blue"
                          : transfer.status === "cancelled"
                          ? "red"
                          : "yellow"
                      }-100 rounded-full p-3`}
                    >
                      <FaUser
                        className={`w-6 h-6 text-${
                          transfer.status === "confirmed"
                            ? "green"
                            : transfer.status === "completed"
                            ? "blue"
                            : transfer.status === "cancelled"
                            ? "red"
                            : "yellow"
                        }-600`}
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {transfer.name} {transfer.surname}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:space-x-4">
                        <p className="text-sm text-gray-500 flex items-center">
                          <FaEnvelope className="w-4 h-4 mr-1" />{" "}
                          {transfer.email}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center">
                          <FaPhoneAlt className="w-4 h-4 mr-1" />{" "}
                          {transfer.phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={transfer.status}
                      onChange={(e) =>
                        handleTransferStatusUpdate(transfer._id, e.target.value)
                      }
                      disabled={updatingTransferStatus === transfer._id}
                      className={`relative inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                        transfer.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : transfer.status === "cancelled"
                          ? "bg-red-100 text-red-800"
                          : transfer.status === "confirmed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="confirmed">✓ Confirmed</option>
                      <option value="completed">★ Completed</option>
                      <option value="cancelled">✖ Cancelled</option>
                    </select>

                    {transfer.assignedDriver ? (
                      <button
                        onClick={() => handleUnassignDriver(transfer._id)}
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        <FaUserTie className="mr-2" /> Change Driver
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          handleAssignDriverToTransfer(transfer._id)
                        }
                        disabled={assigningDriverToTransfer === transfer._id}
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                      >
                        {assigningDriverToTransfer === transfer._id ? (
                          <span className="flex items-center">
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Assigning...</span>
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <FaUserTie className="mr-2" />
                            Assign Driver
                          </span>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => handleTransferDelete(transfer._id)}
                      disabled={deletingTransferId === transfer._id}
                      className="inline-flex items-center px-3 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Transfer Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                  {" "}
                  {/* Transfer Type & Vehicle */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Transfer Details
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded-full">
                          {transfer.serviceType === "one-way"
                            ? "One Way"
                            : "Round Trip"}
                        </span>
                        <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
                          {transfer.carType.charAt(0).toUpperCase() +
                            transfer.carType.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Passengers:</span>
                        <span className="font-medium">{transfer.persons}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Luggage:</span>
                        <span className="font-medium">{transfer.luggage}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Payment:</span>
                        <span className="font-medium capitalize">
                          {transfer.paymentMethod.replace("-", " ")}
                        </span>
                      </div>
                      {/* Pricing Information */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Price:</span>
                          <span className="font-bold text-green-600">
                            {transfer.priceTotal || "N/A"}{" "}
                            {transfer.priceTotal ? "€" : ""}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                          <span className="text-gray-500">Season:</span>
                          <span className="font-medium capitalize">
                            {transfer.season
                              ? `${
                                  transfer.season.charAt(0).toUpperCase() +
                                  transfer.season.slice(1)
                                } Season`
                              : "N/A"}
                          </span>
                        </div>
                        {transfer.partner && (
                          <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-500">Partner:</span>
                            <span className="font-medium">
                              {typeof transfer.partner === "object"
                                ? transfer.partner.firstName ||
                                  transfer.partner.companyName
                                : "Yes"}
                            </span>
                          </div>
                        )}
                        {(transfer.partnerCommission ||
                          transfer.partnerCommission === 0) && (
                          <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-500">Commission:</span>
                            <span className="font-medium text-blue-600">
                              {transfer.partnerCommission.toFixed(2)} €
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Locations */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Locations
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex text-sm">
                        <div className="mr-2 flex flex-col items-center">
                          <div className="w-6 h-6 rounded-full border-2 border-green-500 flex items-center justify-center">
                            <FaMapMarkerAlt className="text-green-500 w-3 h-3" />
                          </div>
                          <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Pickup</p>
                          <p className="text-gray-600">
                            {transfer.pickupLocation}
                          </p>
                        </div>
                      </div>
                      <div className="flex text-sm">
                        <div className="mr-2 flex flex-col items-center">
                          <div className="w-6 h-6 rounded-full border-2 border-red-500 flex items-center justify-center">
                            <FaMapMarkerAlt className="text-red-500 w-3 h-3" />
                          </div>
                          {transfer.serviceType === "round-trip" && (
                            <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Drop-off</p>
                          <p className="text-gray-600">
                            {transfer.dropoffLocation}
                          </p>
                        </div>
                      </div>
                      {transfer.serviceType === "round-trip" &&
                        transfer.returnLocation && (
                          <div className="flex text-sm">
                            <div className="mr-2 flex flex-col items-center">
                              <div className="w-6 h-6 rounded-full border-2 border-purple-500 flex items-center justify-center">
                                <FaMapMarkerAlt className="text-purple-500 w-3 h-3" />
                              </div>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                Return Location
                              </p>
                              <p className="text-gray-600">
                                {transfer.returnLocation}
                              </p>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>{" "}
                  {/* Dates & Times */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Schedule
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="w-4 h-4 text-blue-500 mr-2" />
                        <div>
                          <p className="font-medium text-gray-900">
                            Pickup:{" "}
                            {moment(transfer.pickUpDate).format("DD/MM/YYYY")}
                          </p>
                          <p className="text-gray-500">
                            Time: {transfer.pickupHours}:
                            {transfer.pickupMinutes}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="w-4 h-4 text-red-500 mr-2" />
                        <div>
                          <p className="font-medium text-gray-900">
                            Drop-off:{" "}
                            {moment(transfer.dropOffDate).format("DD/MM/YYYY")}
                          </p>
                          <p className="text-gray-500">
                            Time: {transfer.dropoffHours}:
                            {transfer.dropoffMinutes}
                          </p>
                        </div>
                      </div>
                      {transfer.serviceType === "round-trip" &&
                        transfer.returnDate && (
                          <div className="flex items-center text-sm">
                            <FaCalendarAlt className="w-4 h-4 text-purple-500 mr-2" />
                            <div>
                              <p className="font-medium text-gray-900">
                                Return:{" "}
                                {moment(transfer.returnDate).format(
                                  "DD/MM/YYYY"
                                )}
                              </p>
                              <p className="text-gray-500">
                                Time: {transfer.returnHours}:
                                {transfer.returnMinutes}
                              </p>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                  {/* Driver Assigned */}
                  <div className="space-y-2">
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        Driver & Partner Assigned
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {(() => {
                          // Check how assignedDriver is provided (as object or ID string)
                          if (!transfer.assignedDriver) {
                            return (
                              <div className="flex items-center text-sm">
                                <FaUserTie className="w-4 h-4 text-gray-400 mr-2" />
                                <p className="font-medium text-gray-500">
                                  No Driver Assigned
                                </p>
                              </div>
                            );
                          } else if (
                            typeof transfer.assignedDriver === "object"
                          ) {
                            // If driver info is already an object
                            return (
                              <div className="flex items-center text-sm">
                                <FaUserTie className="w-4 h-4 text-green-500 mr-2" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {transfer.assignedDriver.name}{" "}
                                    {transfer.assignedDriver.surname}
                                  </p>
                                  <p className="text-gray-500 text-xs mt-1">
                                    {transfer.assignedDriver.phone}
                                  </p>
                                </div>
                              </div>
                            );
                          } else {
                            // If driver is just an ID, find the driver in driversData
                            const driverId = transfer.assignedDriver;
                            const driver = driversData.find(
                              (d) => d._id === driverId
                            );

                            if (driver) {
                              return (
                                <div className="flex items-center text-sm">
                                  <FaUserTie className="w-4 h-4 text-green-500 mr-2" />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {driver.name} {driver.surname}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-1">
                                      {driver.phone}
                                    </p>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center text-sm">
                                  <FaUserTie className="w-4 h-4 text-yellow-500 mr-2" />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      Driver ID: {driverId}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-1">
                                      Driver details not available
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          }
                        })()}
                        {(() => {
                          // Check if partner info is available as an object
                          if (transfer.partner) {
                            return (
                              <div className="flex items-center text-sm">
                                <FaUserTie className="w-4 h-4 text-purple-500 mr-2" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {transfer.partner.firstName ||
                                      transfer.partner.companyName}
                                  </p>
                                  <p className="text-gray-500 text-xs mt-1">
                                    {transfer.partner.email}
                                  </p>
                                  <p className="text-gray-500 text-xs mt-1">
                                    {transfer.partner.phone}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          // Check if createdByPartner ID exists
                          else if (transfer.createdByPartner) {
                            // Find the partner in partnersData using the ID
                            const partner = partnersData.find(
                              (p) => p._id === transfer.createdByPartner
                            );

                            if (partner) {
                              return (
                                <div className="flex items-center text-sm mt-7">
                                  <FaUserTie className="w-4 h-4 text-purple-500 mr-2" />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {partner.firstName || partner.companyName}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-1">
                                      {partner.email}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-1">
                                      {partner.phone}
                                    </p>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center text-sm">
                                  <FaUserTie className="w-4 h-4 text-yellow-500 mr-2" />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      Partner ID: {transfer.createdByPartner}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-1">
                                      Partner details not found
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          }
                          // No partner information available
                          else {
                            return (
                              <div className="flex items-center text-sm">
                                <FaUserTie className="w-4 h-4 text-gray-400 mr-2" />
                                <p className="font-medium text-gray-500">
                                  No Partner Available
                                </p>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                    {/* Partner if exists */}
                  </div>
                  {/* Additional Options */}
                </div>
              </div>

              {/* If a tour booking has a transfer, show it here */}
              {(() => {
                // Find tour bookings that are linked to this transfer
                const linkedTourBookings = tourBookingsData.filter(
                  (booking) =>
                    booking.transfer && booking.transfer._id === transfer._id
                );

                if (linkedTourBookings.length > 0) {
                  return (
                    <div className="mt-4 m-6 bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                      <h4 className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
                        <FaGlobe className="mr-2 text-indigo-600" />
                        Connected Tour Booking
                      </h4>
                      {linkedTourBookings.map((booking) => (
                        <div
                          key={booking._id}
                          className="flex flex-col space-y-2"
                        >
                          <div className="flex items-center">
                            <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center mr-2">
                              <FaSuitcase className="text-indigo-600 w-3 h-3" />
                            </div>
                            <div>
                              <p className="font-medium">{booking.tour.name}</p>
                              <div className="flex space-x-4">
                                <p className="text-gray-600 text-sm">
                                  {moment(booking.tourDate).format(
                                    "DD/MM/YYYY"
                                  )}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  Persons: {booking.numberOfPersons}
                                </p>
                                <p className="text-gray-600 text-sm font-semibold">
                                  €{booking.totalPrice}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="ml-7">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                booking.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : booking.status === "confirmed"
                                  ? "bg-blue-100 text-blue-800"
                                  : booking.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {booking.status.charAt(0).toUpperCase() +
                                booking.status.slice(1)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Additional Options */}
              <div className="m-6 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Additional Services
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    {transfer.airportTicketId && (
                      <div className="flex items-center text-sm">
                        <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center mr-2">
                          <FaTruckMoving className="text-blue-600 w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium">Flight/Ferry Number</p>
                          <p className="text-gray-600">
                            {transfer.airportTicketId}
                          </p>
                        </div>
                      </div>
                    )}
                    {transfer.atDisposal && (
                      <div className="flex items-center text-sm">
                        <div className="w-5 h-5 rounded bg-purple-100 flex items-center justify-center mr-2">
                          <FaClock className="text-purple-600 w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium">Driver at Disposal</p>
                          <p className="text-gray-600">
                            For {transfer.disposalHours} hours
                          </p>
                        </div>
                      </div>
                    )}
                    {transfer.helicopterTransfer && (
                      <div className="flex items-center text-sm">
                        <div className="w-5 h-5 rounded bg-red-100 flex items-center justify-center mr-2">
                          <FaTruckMoving className="text-red-600 w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium">Helicopter Transfer</p>
                          <p className="text-gray-600">Requested</p>
                        </div>
                      </div>
                    )}
                    {transfer.assignedDriver && (
                      <div className="flex items-center text-sm mt-4">
                        <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center mr-2">
                          <FaUserTie className="text-green-600 w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium">Assigned Driver</p>
                          <p className="text-gray-600">
                            {transfer.assignedDriver.name}{" "}
                            {transfer.assignedDriver.surname}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {transfer.assignedDriver.phone}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(transfer.additionalInfo || transfer.driverNotes) && (
                <div className="mt-6 px-4 pb-4">
                  <button
                    onClick={() =>
                      setExpandedTransfer(
                        expandedTransfer === transfer._id ? null : transfer._id
                      )
                    }
                    className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    {expandedTransfer === transfer._id ? (
                      <>
                        <FaChevronUp className="mr-1" />
                        Hide Notes
                      </>
                    ) : (
                      <>
                        <FaChevronDown className="mr-1" />
                        Show Notes
                      </>
                    )}
                  </button>

                  <AnimatePresence>
                    {expandedTransfer === transfer._id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 grid grid-cols-1 gap-4">
                          {transfer.additionalInfo && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">
                                Customer Notes
                              </h4>
                              <p className="text-sm text-gray-600">
                                {transfer.additionalInfo}
                              </p>
                            </div>
                          )}

                          {transfer.driverNotes && (
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                              <h4 className="text-sm font-medium text-blue-700 mb-2">
                                Driver Notes
                              </h4>
                              <p className="text-sm text-blue-600">
                                {transfer.driverNotes}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          ))}
      </div>
    </>
  );
}

export default Transfers;
